/* eslint-disable react-refresh/only-export-components -- hooks are co-located with TenantProvider by design */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { initFirebase } from './firebase';
import { setSentryUser, clearSentryUser } from './sentry';

/**
 * Custom claims structure from Cloud Functions
 */
export interface TenantClaims {
  tenantIds: string[];
  roles: Record<string, 'owner' | 'admin' | 'staff'>;
  superadmin?: boolean; // Global admin access to all tenants
}

/**
 * Tenant context state
 */
interface TenantContextState {
  user: User | null;
  claims: TenantClaims | null;
  currentTenantId: string | null;
  loading: boolean;
  error: Error | null;
  /**
   * True when user is authenticated but has no tenants (needs to complete onboarding)
   */
  needsOnboarding: boolean;

  // Actions
  setCurrentTenantId: (tenantId: string | null) => void;
  refreshClaims: () => Promise<void>;
}

const TenantContext = createContext<TenantContextState | null>(null);

/**
 * Provider component for tenant context
 */
export function TenantProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [claims, setClaims] = useState<TenantClaims | null>(null);
  const [currentTenantId, setCurrentTenantIdState] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Load saved tenant from localStorage
  useEffect(() => {
    const savedTenantId = localStorage.getItem('currentTenantId');
    if (savedTenantId) {
      setCurrentTenantIdState(savedTenantId);
    }
  }, []);

  // Listen to auth state and claims updates
  useEffect(() => {
    try {
      const { auth, db } = initFirebase();

      const unsubscribeAuth = onAuthStateChanged(auth, async (currentUser) => {
        setUser(currentUser);

        if (currentUser) {
          // Get initial claims from token
          let tokenResult = await currentUser.getIdTokenResult();
          let userClaims = (tokenResult.claims as unknown) as TenantClaims;

          // If user has email (not anonymous) and has no claims, force refresh immediately
          // (anonymous users won't have claims, but email/password users should)
          if (currentUser.email && (!userClaims.tenantIds || !userClaims.roles)) {
            try {
              // Force token refresh to get latest claims
              await currentUser.getIdToken(true);
              tokenResult = await currentUser.getIdTokenResult();
              userClaims = (tokenResult.claims as unknown) as TenantClaims;
            } catch (err) {
              console.error('Error refreshing token:', err);
            }
          }
          
          // If still no claims, check user document for claimsUpdatedAt
          if (!userClaims.tenantIds || !userClaims.roles) {
            try {
              const userDocRef = doc(db, 'users', currentUser.uid);
              const userDoc = await getDoc(userDocRef);
              if (userDoc.exists() && userDoc.data()?.claimsUpdatedAt) {
                // Force token refresh
                await currentUser.getIdToken(true);
                tokenResult = await currentUser.getIdTokenResult();
                userClaims = (tokenResult.claims as unknown) as TenantClaims;
              }
            } catch (err) {
              console.error('Error checking user document:', err);
            }
          }

          const validClaims = userClaims.tenantIds && userClaims.roles ? userClaims : null;
          setClaims(validClaims);

          // Set default tenant if none selected or if saved tenant is not in claims
          if (validClaims?.tenantIds && validClaims.tenantIds.length > 0) {
            const savedTenantId = localStorage.getItem('currentTenantId');
            let tenantToSet: string | null = null;
            
            // If saved tenant is valid, use it
            if (savedTenantId && validClaims.tenantIds.includes(savedTenantId)) {
              tenantToSet = savedTenantId;
            } else {
              // Otherwise use first available
              tenantToSet = validClaims.tenantIds[0];
            }
            
            if (tenantToSet) {
              setCurrentTenantIdState(tenantToSet);
              localStorage.setItem('currentTenantId', tenantToSet);
            }
          }

          // Set Sentry user context for error attribution
          setSentryUser({
            id: currentUser.uid,
            email: currentUser.email || undefined,
            tenantId: currentTenantId || validClaims?.tenantIds?.[0],
          });

          // Listen to user document for claimsUpdatedAt changes
          const userDocRef = doc(db, 'users', currentUser.uid);
          const unsubscribeUser = onSnapshot(userDocRef, async (docSnapshot) => {
            if (docSnapshot.exists()) {
              const userData = docSnapshot.data();

              // If claimsUpdatedAt changed, force token refresh
              if (userData.claimsUpdatedAt) {
                try {
                  await currentUser.getIdToken(true); // Force refresh
                  const newTokenResult = await currentUser.getIdTokenResult();
                  const newClaims = (newTokenResult.claims as unknown) as TenantClaims;

                  const validNewClaims = newClaims.tenantIds && newClaims.roles ? newClaims : null;
                  setClaims(validNewClaims);

                  // Update tenant selection based on new claims
                  if (validNewClaims?.tenantIds && validNewClaims.tenantIds.length > 0) {
                    const currentTenant = localStorage.getItem('currentTenantId');
                    let tenantToSet: string | null = null;
                    
                    // If current tenant is valid, keep it
                    if (currentTenant && validNewClaims.tenantIds.includes(currentTenant)) {
                      tenantToSet = currentTenant;
                    } else {
                      // Otherwise use first available
                      tenantToSet = validNewClaims.tenantIds[0];
                    }
                    
                    if (tenantToSet) {
                      setCurrentTenantIdState(tenantToSet);
                      localStorage.setItem('currentTenantId', tenantToSet);
                    }
                  }
                } catch (err) {
                  console.error('Error refreshing token:', err);
                }
              }
            }
          });

          setLoading(false);

          return () => unsubscribeUser();
        } else {
          setClaims(null);
          setCurrentTenantIdState(null);
          clearSentryUser();
          setLoading(false);
        }
      }, (authError) => {
        console.error('Auth state change error:', authError);
        setError(authError as Error);
        setLoading(false);
      });

      return () => unsubscribeAuth();
    } catch (initError) {
      console.error('Firebase initialization error:', initError);
      setError(initError as Error);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- currentTenantId intentionally omitted to avoid infinite loops
  }, []);

  // Save tenant selection to localStorage
  const setCurrentTenantId = (tenantId: string | null) => {
    setCurrentTenantIdState(tenantId);
    if (tenantId) {
      localStorage.setItem('currentTenantId', tenantId);
    } else {
      localStorage.removeItem('currentTenantId');
    }
  };

  // Manually refresh claims
  const refreshClaims = async () => {
    if (user) {
      try {
        await user.getIdToken(true); // Force refresh
        const tokenResult = await user.getIdTokenResult();
        const newClaims = (tokenResult.claims as unknown) as TenantClaims;
        const validClaims = newClaims.tenantIds && newClaims.roles ? newClaims : null;
        setClaims(validClaims);
        
        // Set tenant if not set or if current tenant is not in claims
        if (validClaims?.tenantIds && validClaims.tenantIds.length > 0) {
          if (!currentTenantId || !validClaims.tenantIds.includes(currentTenantId)) {
            setCurrentTenantIdState(validClaims.tenantIds[0]);
          }
        }
      } catch (err) {
        console.error('Error refreshing claims:', err);
        throw err;
      }
    }
  };

  // User needs onboarding if authenticated but has no tenants (and is not superadmin)
  const needsOnboarding = !!(
    user &&
    !loading &&
    !claims?.superadmin &&
    (!claims?.tenantIds || claims.tenantIds.length === 0)
  );

  const value: TenantContextState = {
    user,
    claims,
    currentTenantId,
    loading,
    error,
    needsOnboarding,
    setCurrentTenantId,
    refreshClaims,
  };

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

/**
 * Hook to access tenant context
 */
export function useTenant() {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within TenantProvider');
  }
  return context;
}

/**
 * Hook to get current user's role for the current tenant
 */
export function useCurrentRole() {
  const { claims, currentTenantId } = useTenant();

  if (!currentTenantId || !claims?.roles) {
    return null;
  }

  return claims.roles[currentTenantId] || null;
}

/**
 * Hook to check if user has specific role(s)
 */
export function useHasRole(...roles: Array<'owner' | 'admin' | 'staff'>) {
  const currentRole = useCurrentRole();
  return currentRole ? roles.includes(currentRole) : false;
}

/**
 * Hook to check if current user is a superadmin
 */
export function useIsSuperAdmin() {
  const { claims } = useTenant();
  return claims?.superadmin === true;
}

/**
 * Hook to check if user has tenant access (either through claims or superadmin)
 */
export function useHasTenantAccess(tenantId: string | null) {
  const { claims } = useTenant();

  if (!tenantId) {
    return false;
  }

  // Superadmins have access to all tenants
  if (claims?.superadmin) {
    return true;
  }

  // Regular users need the tenant in their tenantIds
  return claims?.tenantIds?.includes(tenantId) ?? false;
}
